/**
 * Provides utility functions for managing contact methods and interactions.
 *
 * @function useContacts
 * @returns {Object} An object with contact-related methods and utilities.
 *
 * @description
 * This composable offers:
 * - Methods to fetch contact pages and methods
 * - Link opening functionality
 * - Contact method validation
 *
 * @example
 * const { getContactsPage, openLink, isChatMethod } = useContacts()
 */
export function useContacts() {
  const localePath = useLocalePath()
  const { openDialog } = useDialog()
  const { insideChat } = useInsideChat()

  /**
   * Fetches contact page data from CMS.
   *
   * @async
   * @function getContactsPage
   * @param {string} deliveryKey - Unique key for retrieving contact page content
   * @returns {Promise<Object>} Fetched contact page data
   */
  const getContactsPage = async (deliveryKey: string) => {
    const query = {
      locale: localePath(''),
      vse: useRoute().query.vse,
      contentId: useRoute().query.contentId,
      deliveryKey: deliveryKey,
    }

    return useFetch('/api/cms/getContacts', {
      query,
    })
  }

  /**
   * Retrieves all contact methods from CMS.
   *
   * @async
   * @function getAllContacts
   * @param {string} deliveryKey - Unique key for retrieving all contact methods
   * @returns {Promise<Object>} Fetched contact methods data
   */
  const getAllContacts = async (deliveryKey: string) => {
    const query = {
      locale: localePath(''),
      vse: useRoute().query.vse,
      contentId: useRoute().query.contentId,
      deliveryKey: deliveryKey,
    }

    return useFetch('/api/cms/getAllContacts', {
      query,
    })
  }

  /**
   * Opens a link based on the specified type.
   *
   * @function openLink
   * @param {string} cta - Call-to-action link or identifier
   * @param {string} [type='slug'] - Type of link navigation
   * @description
   * Supports different navigation types:
   * - 'anchor': Navigate to a page hash
   * - 'sidebar' or 'modal': Open a dialog
   * - 'slug' (default): Navigate to a localized route
   */
  const openLink = (cta: string, type: string = 'slug') => {
    switch (type) {
      case 'anchor':
        return navigateTo({ hash: cta })
      // @ts-ignore
      case 'sidebar' || 'modal':
        return openDialog(cta)
      default:
        return navigateTo(localePath(cta))
    }
  }

  /**
   * Checks if a contact method is a chat method.
   *
   * @function isChatMethod
   * @param {string} methodName - Name of the contact method
   * @returns {boolean} Whether the method is a chat method
   */
  const isChatMethod = (methodName: string) =>
    methodName.toLowerCase() === 'chat'

  /**
   * Validates a contact method's availability.
   *
   * @function isContactMethodValid
   * @param {string} methodName - Name of the contact method
   * @returns {boolean} Whether the contact method is valid and available
   */
  const isContactMethodValid = (methodName: string) => {
    return !isChatMethod(methodName) || insideChat.value.isAvailable
  }

  return {
    getContactsPage,
    getAllContacts,
    openLink,
    isChatMethod,
    isContactMethodValid,
  }
}
